import React, { useState, useMemo } from 'react';
import { Form, Field } from 'react-final-form';
import { useTranslation } from 'core/contexts/localization';
import { useStyles } from '../styles';
import { UnitType } from '@utriper/core';
import { SelectComponent, TextInput, CheckboxComponent } from '@utriper/core-ui';
import { Box, Typography, Button, FormHelperText } from '@mui/material';

const UnitForm = ({ unit, onSubmit, onCancel }) => {
    const { t: translate, i18n } = useTranslation();
    const classes = useStyles();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const unitOptions = useMemo(
        () =>
            UnitType?.getIds().map((id) => {
                const translated = UnitType.getTranslatedName(UnitType.types, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        [i18n.language]
    );

    const initialValues = unit
        ? {
              typeId: unit.typeId === undefined ? '' : unit.typeId,
              label: unit.label || '',
              description: unit.description || '',
              minAge: unit.minAge || '',
              maxAge: unit.maxAge || '',
              isActive: unit.isActive !== undefined ? unit.isActive : true,
          }
        : {
              typeId: '',
              label: '',
              description: '',
              minAge: '',
              maxAge: '',
              isActive: true,
          };

    const validate = (values) => {
        const errors = {};
        // Required fields
        if (!values.typeId === null || values.typeId === '') {
            errors.typeId = translate('units.validation.type_id_required');
        }

        if (!values.label?.trim()) {
            errors.label = translate('units.validation.label_required');
        }

        // Age validation
        if (values.minAge && (isNaN(values.minAge) || values.minAge < 0)) {
            errors.minAge = translate('units.validation.invalid_age');
        }

        if (values.maxAge && (isNaN(values.maxAge) || values.maxAge < 0)) {
            errors.maxAge = translate('units.validation.invalid_age');
        }

        // Age range validation
        if (values.minAge && values.maxAge && parseInt(values.minAge) > parseInt(values.maxAge)) {
            errors.maxAge = translate('units.validation.age_range_invalid');
        }

        return errors;
    };

    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            // Clean up the data
            const cleanValues = {
                typeId: values.typeId,
                label: values.label?.trim(),
                description: values.description?.trim() || null,
                minAge: values.minAge ? parseInt(values.minAge) : null,
                maxAge: values.maxAge ? parseInt(values.maxAge) : null,
                isActive: values.isActive,
            };

            await onSubmit(cleanValues);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validate={validate}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} className={classes.formContainer}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box>
                            <Field name="typeId">{({ input, meta }) => <SelectComponent id="typeId" input={input} values={unitOptions} label={translate('units.form.type_id')} labelId="typeId-label" style={{ width: '100%' }} disabled={isSubmitting} hasError={meta.touched && meta.error} errorMessage={meta.touched && meta.error ? meta.error : ''} />}</Field>
                        </Box>
                        <Box>
                            <Field name="label" component={TextInput} label={translate('units.form.label')} placeholder={translate('units.form.label_placeholder')} style={{ width: '100%' }} disabled={isSubmitting} />
                        </Box>

                        <Box>
                            <Field name="description" component={TextInput} label={translate('units.form.description')} placeholder={translate('units.form.description_placeholder')} multiline rows={3} style={{ width: '100%' }} className={classes.formField} disabled={isSubmitting} />
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {translate('units.form.min_age')} / {translate('units.form.max_age')}
                            </Typography>
                            <Box className={classes.ageRangeContainer}>
                                <Field name="minAge" component={TextInput} label={translate('units.form.min_age')} placeholder={translate('units.form.min_age_placeholder')} type="number" className={`${classes.formField} ${classes.ageField}`} disabled={isSubmitting} />
                                <Field name="maxAge" component={TextInput} label={translate('units.form.max_age')} placeholder={translate('units.form.max_age_placeholder')} type="number" className={`${classes.formField} ${classes.ageField}`} disabled={isSubmitting} />
                            </Box>
                        </Box>

                        <Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                                <Field name="isActive" labelComponent={translate('units.form.is_active')} component={CheckboxComponent} disabled={isSubmitting} />
                                <FormHelperText variant="caption" className={classes.helpText}>
                                    {translate('units.form.is_active_help')}
                                </FormHelperText>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.formActions}>
                        <Button onClick={onCancel} disabled={isSubmitting} variant="outlined">
                            {translate('form.cancel')}
                        </Button>
                        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                            {isSubmitting ? translate('utils.loading') : unit ? translate('form.save') : translate('units.create_unit')}
                        </Button>
                    </Box>
                </form>
            )}
        />
    );
};

UnitForm.displayName = 'UnitForm';

export default UnitForm;
